// src/pages/msp-inventory/pages/reports/ExpirationsReportPage.jsx
import React, { useEffect, useState } from "react";
import { Calendar } from "lucide-react";
import ClickableSkuCell from "../../components/reports/ClickableSkuCell";
import { SortableTH, Pagination } from "../../components/reports/TableControls";
import { getExpirationsReport } from "../../services/catalogService";
import { useImageBackfill } from "../../hooks/useImageBackfill";
import { useSortAndPaginate } from "../../hooks/useSortAndPaginate";
import ExportCsvButton from "../../components/reports/ExportCsvButton";
import { exportSkuReportCsv } from "../../utils/exportCsv";
import { buildCacheKey, getCachedReport, setCachedReport, invalidateCachedReport } from "../../utils/reportCache";
import RefreshReportBar from "../../components/reports/RefreshReportBar";
import LoadingGame from "../../components/reports/LoadingGame";

const URGENCY_STYLES = {
    expired: "bg-gray-800 text-white",
    critical: "bg-red-50 text-red-600",
    warning: "bg-yellow-50 text-yellow-700",
    ok: "bg-green-50 text-green-600",
};
const URGENCY_LABELS = { expired: "Expired", critical: "Critical", warning: "Warning", ok: "OK" };

const ExpirationsReportPage = ({ onOpenSku }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    const cacheKey = buildCacheKey("expirations", {});

    const loadReport = async (forceRefresh = false) => {
        const isInitialLoad = !data;

        if (!forceRefresh) {
            const cached = await getCachedReport(cacheKey);
            if (cached) {
                setData(cached.data);
                setSavedAt(cached.savedAt);
                setLoading(false);
                return;
            }
        }

        if (isInitialLoad) setLoading(true);
        else setRefreshing(true);
        try {
            const d = await getExpirationsReport();
            setData(d);
            setSavedAt(Date.now());
            setCachedReport(cacheKey, d);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        loadReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleForceRefresh = async () => {
        await invalidateCachedReport(cacheKey);
        await loadReport(true);
    };

    const patchRow = (id, patch) => {
        setData((prev) => prev ? {
            ...prev,
            rows: prev.rows.map((r) => r.catalog_item_id === id ? { ...r, ...patch } : r),
        } : prev);
    };

    const { sorted, pageItems, page, setPage, totalPages, sortConfig, requestSort, pageSize } =
        useSortAndPaginate(data?.rows || [], { key: "days_until_expiration", dir: "asc" }, "reports:expirations:table");

    useImageBackfill(sorted, patchRow);

    return (
        <div className="max-w-5xl mx-auto py-8 px-6">
            <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <Calendar size={22} className="text-yellow-500" /> Expiring Inventory
                    </h1>
                    <p className="text-gray-500 text-sm">Items approaching (or past) their expiration date</p>
                </div>
                <RefreshReportBar savedAt={savedAt} refreshing={refreshing} onRefresh={handleForceRefresh} onAutoRefresh={handleForceRefresh} />
            </div>

            {(data?.rows || []).length > 0 && (
                <div className="flex justify-end mb-4">
                    <ExportCsvButton onExport={() => exportSkuReportCsv({
                        filename: `expirations_${new Date().toISOString().slice(0, 10)}.csv`,
                        rows: sorted,
                        idKey: "catalog_item_id",
                        headers: ["Item", "SKU", "Expiration Date", "Days Until", "Stock", "Status"],
                        getRowValues: (r) => [r.title, r.sku, r.expiration_date, r.days_until_expiration, r.stock, URGENCY_LABELS[r.urgency]],
                    })} />
                </div>
            )}

            {loading ? (
                <LoadingGame label="Loading expirations..." />
            ) : (data?.rows || []).length === 0 ? (
                <div className="bg-white rounded-2xl shadow-lg p-10 text-center text-gray-400 text-sm">
                    No items have an expiration date set.
                </div>
            ) : (
                <div className="bg-white rounded-2xl shadow-lg p-5 overflow-x-auto max-h-[70vh] overflow-y-auto">
                    <table className="w-full text-xs">
                        <thead className="sticky top-0 z-10 bg-white">
                            <tr className="text-left text-gray-400 border-b border-gray-100">
                                <SortableTH label="Item" sortKey="title" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Expiration Date" sortKey="expiration_date" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Days Until" sortKey="days_until_expiration" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Stock" sortKey="stock" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Status" sortKey="urgency" sortConfig={sortConfig} onSort={requestSort} />
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.map((r) => (
                                <tr key={r.catalog_item_id} className="border-b border-gray-50">
                                    <td className="py-2"><ClickableSkuCell item={r} onOpen={onOpenSku} /></td>
                                    <td className="py-2">{r.expiration_date}</td>
                                    <td className="py-2 font-bold">{r.days_until_expiration < 0 ? `${Math.abs(r.days_until_expiration)}d ago` : `${r.days_until_expiration}d`}</td>
                                    <td className="py-2">{r.stock}</td>
                                    <td className="py-2">
                                        <span className={`px-2 py-0.5 rounded-full font-bold ${URGENCY_STYLES[r.urgency]}`}>
                                            {URGENCY_LABELS[r.urgency]}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <Pagination page={page} totalPages={totalPages} setPage={setPage} totalItems={sorted.length} pageSize={pageSize} />
                </div>
            )}
        </div>
    );
};

export default ExpirationsReportPage;